import React, { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { useToast } from 'context/ToastContext';
import SettingsLayout from '../SettingsLayout/SettingsLayout';
import { setAppTitle } from 'utils/utils';
import { SpinnerComponent } from 'shared/SpinnerComponent';
import { getCaptureNowIntegrationId, generateCaptureNowIntegrationId } from 'services';
import { toastConstant } from 'constants/toastmessage';

const CaptureNowIntegration = () => {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [captureNowId, setCaptureNowId] = useState(null);

  useEffect(() => {
    setAppTitle('Settings', 'CaptureNow');
    fetchCaptureNowId();
    return () => setAppTitle();
  }, []);

  const fetchCaptureNowId = async () => {
    setLoading(true);
    try {
      const response = await getCaptureNowIntegrationId();
      console.log('response', response);
      setCaptureNowId(response.data?.capturenow_integration_id);
    } catch (error) {
      console.error('Error fetching CaptureNow ID:', error);
      setCaptureNowId(null);
    } finally {
      setLoading(false);
    }
  };

  const generateCaptureNowId = async () => {
    setGenerating(true);
    try {
      const response = await generateCaptureNowIntegrationId();
      console.log('generate response:', response);
      console.log('response type:', typeof response);
      console.log('response keys:', Object.keys(response || {}));

      // Handle different response structures
      const newId = response?.data?.capturenow_integration_id || response?.capturenow_integration_id;

      console.log('extracted ID:', newId);

      if (newId) {
        setCaptureNowId(newId);
        console.log('ID set in state:', newId);
        addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, 'CaptureNow ID generated successfully');
      } else {
        throw new Error('No integration ID received in response');
      }
    } catch (error) {
      console.error('Error generating CaptureNow ID:', error);
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, 'Failed to generate CaptureNow ID. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(captureNowId);
      addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.CLIPBOARD_COPY_SUCCCESS_GENERAL);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = captureNowId;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.CLIPBOARD_COPY_SUCCCESS_GENERAL);
    }
  };

  if (loading) {
    return (
      <SettingsLayout>
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <SpinnerComponent />
              </div>
            </div>
          </div>
        </div>
      </SettingsLayout>
    );
  }

  return (
    <SettingsLayout>
      <div className="container-fluid">
        <div className="row">
          <div className="col-12 mb-5">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h1 className="title gray-800 mb-0 fs-3 fs-md-1">CaptureNow Integration</h1>
            </div>

            <Card className="mb-4 card-bg">
              <h5 className="caption-bold">Integration Status</h5>
              {captureNowId ? (
                <div className="row">
                  <div className="col-md-8">
                    <div className="alert alert-success">
                      <i className="pi pi-check-circle me-2"></i>
                      <strong>Integration Active</strong>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-bold">Your CaptureNow Integration ID:</label>
                      <div className="d-flex align-items-center gap-2">
                        <code className="bg-light p-2 rounded flex-grow-1">{captureNowId}</code>
                        <Button
                          icon="pi pi-copy"
                          className="p-button-outlined rounded"
                          onClick={copyToClipboard}
                          tooltip="Copy to clipboard"
                        />
                      </div>
                    </div>
                    <p className="text-muted">
                      <i className="pi pi-info-circle me-2"></i>
                      This ID is unique to your firm and should be shared with CaptureNow for integration setup.
                    </p>
                  </div>
                  <div className="col-md-4">
                    <div className="text-center">
                      <i className="pi pi-check-circle text-success" style={{ fontSize: '3rem' }}></i>
                      <p className="mt-2 text-success fw-bold">Integration Ready</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="row">
                  <div className="col-md-8">
                    <div className="alert alert-warning">
                      <i className="pi pi-exclamation-triangle me-2"></i>
                      <strong>No Integration ID Found</strong>
                    </div>
                    <p className="text-muted mb-3">
                      You need to generate a unique CaptureNow integration ID to connect your firm with CaptureNow services.
                    </p>
                    <Button
                      label="Generate CaptureNow ID"
                      icon="pi pi-plus"
                      className="p-button-primary"
                      onClick={generateCaptureNowId}
                      loading={generating}
                    />
                    <div className="mt-3 p-3 bg-light rounded">
                      <p className="mb-0 text-muted">
                        <i className="pi pi-info-circle me-2"></i>
                        <strong>Note:</strong> This will generate a unique ID that should be shared with CaptureNow. The ID will be unique
                        for your firm and cannot be changed once generated.
                      </p>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="text-center">
                      <i className="pi pi-exclamation-triangle text-warning" style={{ fontSize: '3rem' }}></i>
                      <p className="mt-2 text-warning fw-bold">Setup Required</p>
                    </div>
                  </div>
                </div>
              )}
            </Card>

            <Card className="card-bg">
              <h5 className="caption-bold">About CaptureNow Integration</h5>
              <div className="row">
                <div className="col-md-6">
                  <h6 className="sub-title">What is CaptureNow?</h6>
                  <p className="text-muted">
                    CaptureNow is a call recording and transcription service that integrates with your law practice management system to
                    automatically capture and store important client communications.
                  </p>
                </div>
                <div className="col-md-6">
                  <h6 className="sub-title">How it works</h6>
                  <ul className="text-muted">
                    <li>Generate a unique integration ID</li>
                    <li>Share the ID with CaptureNow</li>
                    <li>CaptureNow will configure your account</li>
                    <li>Start recording and transcribing calls automatically</li>
                  </ul>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </SettingsLayout>
  );
};

export default CaptureNowIntegration;
